import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Clock3, Hash, Search, Trash2, UserCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import { apiFetch } from '../../lib/api';
import type { AppDispatch } from '../../store';
import { openTicketPortal } from '../../store/features/ticketPortal/ticketPortalSlice';
import {
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';
import PortalAvatar from '../profile/PortalAvatar';

type Props = {
  open: boolean;
  onClose: () => void;
};

type UserSearchResult = {
  kind: 'user';
  ref: string;
  uid: string | null;
  email: string | null;
  name: string;
  firstName: string | null;
  lastName: string | null;
  photoURL: string | null;
  role: string | null;
  businessName: string | null;
};

type TicketSearchResult = {
  kind: 'ticket';
  ref: string;
  id: string;
  ticketNumber: string | null;
  title: string;
  status: string | null;
  priority: string | null;
  orgName: string | null;
  projectName: string | null;
  updatedAt: string | null;
};

type SearchResult = UserSearchResult | TicketSearchResult;

type HistoryEntry = UserSearchResult & {
  searchedAt: number;
};

const HISTORY_KEY = 'cybard:portal-global-search-history:v1';
const HISTORY_LIMIT = 12;

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeResult(item: Record<string, unknown>): UserSearchResult | null {
  const email = asText(item.email);
  const uid = asText(item.uid ?? item.id);
  const ref = uid || email;
  if (!ref) return null;

  const firstName = asText(item.firstName) || null;
  const lastName = asText(item.lastName) || null;
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const explicitName = asText(item.name ?? item.displayName);
  const businessName = asText(item.businessName) || null;

  const name = fullName || explicitName || email || 'User';
  const role = asText(item.role) || null;
  const photoURL =
    asText(
      item.photoURL ??
        item.avatarUrl ??
        item.avatarURL ??
        item.avatar ??
        item.imageUrl ??
        item.image,
    ) || null;

  return {
    kind: 'user',
    ref,
    uid: uid || null,
    email: email || null,
    name,
    firstName,
    lastName,
    photoURL,
    role,
    businessName,
  };
}

function normalizeTicketResult(item: Record<string, unknown>): TicketSearchResult | null {
  const id = asText(item.id);
  if (!id) return null;
  const ticketNumber = asText(item.ticketNumber) || null;
  const title = asText(item.title) || 'Untitled ticket';
  const status = asText(item.status) || null;
  const priority = asText(item.priority) || null;
  const orgName = asText(item.orgName) || null;
  const projectName = asText(item.projectName) || null;
  const updatedAt = asText(item.updatedAt) || null;
  return {
    kind: 'ticket',
    ref: `ticket:${id}`,
    id,
    ticketNumber,
    title,
    status,
    priority,
    orgName,
    projectName,
    updatedAt,
  };
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const base = normalizeResult(item as Record<string, unknown>);
        if (!base) return null;
        const searchedAt =
          typeof (item as any).searchedAt === 'number' ? (item as any).searchedAt : Date.now();
        return { ...base, searchedAt };
      })
      .filter((item): item is HistoryEntry => Boolean(item))
      .sort((a, b) => b.searchedAt - a.searchedAt)
      .slice(0, HISTORY_LIMIT);
  } catch {
    return [];
  }
}

function persistHistory(entries: HistoryEntry[]) {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_LIMIT)));
  } catch {
    // noop
  }
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 4, 12, 0.82);
  backdrop-filter: blur(9px) saturate(95%);
  -webkit-backdrop-filter: blur(9px) saturate(95%);
  z-index: 1200;
`;

const Panel = styled.section`
  position: fixed;
  inset: 0;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-radius: 0;
  border: none;
  z-index: 1201;
  overflow: auto;
  padding: clamp(14px, 3.2vh, 28px) 0 0;
`;

const Column = styled.div`
  width: min(840px, calc(100% - 28px));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Head = styled.div`
  display: block;
  padding: 0;
`;

const SearchWrap = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 0 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 0;
  background: transparent;
  color: ${cyberGlassTextPrimary};
`;

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${cyberGlassTextPrimary};
  font-size: 1rem;
  font-weight: 700;
  &::placeholder {
    color: ${cyberGlassTextMuted};
  }
`;

const CloseBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-grid;
  place-items: center;
  color: ${cyberGlassTextMuted};
  width: 36px;
  height: 36px;
  &:hover {
    color: ${cyberGlassTextPrimary};
  }
`;

const CornerCloseBtn = styled(CloseBtn)`
  position: fixed;
  top: clamp(18px, 3.6vh, 34px);
  right: clamp(24px, 8vw, 80px);
  z-index: 1202;
`;

const CornerCloseImage = styled.img`
  width: 75px;
  height: 75px;
  object-fit: contain;
  display: block;
  opacity: 0.9;
`;

const Body = styled.div`
  min-height: 0;
  max-height: calc(100vh - 150px);
  overflow: auto;
  padding: 0 0 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Title = styled.h3`
  margin: 0;
  color: ${cyberGlassTextPrimary};
  font-size: 0.98rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const ClearBtn = styled.button`
  all: unset;
  cursor: pointer;
  color: ${cyberGlassTextMuted};
  font-size: 0.82rem;
  font-weight: 800;
  display: inline-grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
  &:hover {
    color: ${cyberGlassTextPrimary};
  }
`;

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ResultRow = styled.button<{ $active?: boolean }>`
  all: unset;
  cursor: pointer;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-radius: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: ${cyberGlassTextPrimary};

  &:hover {
    background: rgba(255, 255, 255, 0.03);

  }
`;

const ResultMeta = styled.div`
  min-width: 0;
  display: grid;
  gap: 3px;
`;

const ResultName = styled.div`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 900;
  font-size: 0.94rem;
`;

const ResultSub = styled.div`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${cyberGlassTextMuted};
  font-size: 0.82rem;
  font-weight: 700;
`;

const Empty = styled.div`
  padding: 4px 0 0;
  color: ${cyberGlassTextMuted};
  font-weight: 600;
  font-size: 0.9rem;
`;

const SearchState = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.35;
`;

export default function GlobalSearchModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { role, user } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const canSearchUsers = role === 'admin' || role === 'agent';
  const ticketRouteBase = role === 'client' ? '/dashboard/tickets' : '/dashboard/list';

  const historyItems = useMemo(
    () =>
      history.map((item) => ({
        ...item,
      })),
    [history],
  );

  const upsertHistory = (result: UserSearchResult) => {
    const next: HistoryEntry = { ...result, searchedAt: Date.now() };
    const merged = [
      next,
      ...history.filter((entry) => entry.ref.toLowerCase() !== result.ref.toLowerCase()),
    ].slice(0, HISTORY_LIMIT);
    setHistory(merged);
    persistHistory(merged);
  };

  const goToProfile = (result: UserSearchResult) => {
    const selfUid = asText(user?.uid);
    const selfEmail = asText(user?.email).toLowerCase();
    const targetUid = asText(result.uid);
    const targetEmail = asText(result.email).toLowerCase();
    const targetRef = asText(result.ref);
    const isSelf =
      (targetUid && targetUid === selfUid) || (targetEmail && targetEmail === selfEmail) || !targetRef;
    if (isSelf) {
      navigate('/dashboard/profile');
    } else {
      navigate(`/dashboard/users/profile/${encodeURIComponent(targetRef)}`);
    }
    upsertHistory(result);
    setQuery('');
    onClose();
  };

  const openTicket = (result: TicketSearchResult) => {
    setQuery('');
    try { window.dispatchEvent(new CustomEvent('modals:close-all')); } catch {}
    onClose();
    if (role === 'admin' || role === 'agent') {
      dispatch(openTicketPortal({ ticketId: result.id }));
      return;
    }
    navigate(`${ticketRouteBase}?focusTicket=${encodeURIComponent(result.id)}`);
  };

  const openResult = (result: SearchResult) => {
    if (result.kind === 'ticket') {
      openTicket(result);
      return;
    }
    goToProfile(result);
  };

  useEffect(() => {
    if (!open) return;
    setHistory(loadHistory());
    setActiveIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      setActiveIndex(0);
      return;
    }

    let cancelled = false;
    const id = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        if (!canSearchUsers) {
          const me = normalizeResult({
            uid: user?.uid,
            email: user?.email,
            name: user?.displayName,
            photoURL: user?.photoURL,
            role,
          });
          const haystack = `${asText(user?.displayName)} ${asText(user?.email)}`.toLowerCase();
          const needle = trimmed.toLowerCase();
          const local = me && haystack.includes(needle) ? [me] : [];
          if (!cancelled) setResults(local);
          return;
        }

        const params = new URLSearchParams({ q: trimmed, page: '0' });
        const [usersData, ticketsData] = await Promise.all([
          apiFetch<{ users?: Array<Record<string, unknown>> }>(`/api/admin/users?${params.toString()}`),
          apiFetch<{ tickets?: Array<Record<string, unknown>> }>(
            `/api/admin/tickets/search?${new URLSearchParams({
              q: trimmed,
              limit: '20',
            }).toString()}`,
          ),
        ]);
        const usersList = Array.isArray(usersData?.users) ? usersData.users : [];
        const ticketList = Array.isArray(ticketsData?.tickets) ? ticketsData.tickets : [];
        const mappedUsers = usersList
          .map((item) => normalizeResult(item))
          .filter((item): item is UserSearchResult => Boolean(item));
        const mappedTickets = ticketList
          .map((item) => normalizeTicketResult(item))
          .filter((item): item is TicketSearchResult => Boolean(item));
        const mapped = [...mappedTickets, ...mappedUsers].slice(0, 40);
        if (!cancelled) {
          setResults(mapped);
          setActiveIndex(0);
        }
      } catch (e) {
        if (!cancelled) {
          setResults([]);
          setError('Could not load search results right now.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [open, query, canSearchUsers, user?.uid, user?.email, user?.displayName, user?.photoURL, role]);

  if (!open) return null;

  const showHistory = query.trim().length < 2;
  const trimmedQuery = query.trim();
  const visibleRows = showHistory ? historyItems : results;

  const clearHistory = () => {
    setHistory([]);
    persistHistory([]);
  };

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <Panel role="dialog" aria-modal="true" aria-label="Global search">
        <CornerCloseBtn type="button" onClick={onClose} aria-label="Close search">
          <CornerCloseImage src="/images/xOut.png" alt="" aria-hidden="true" />
        </CornerCloseBtn>
        <Column>
          <Head>
            <SearchWrap>
              <Search size={18} />
              <SearchInput
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search users, clients, businesses, or ticket #..."
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    setActiveIndex((prev) => Math.min(prev + 1, Math.max(visibleRows.length - 1, 0)));
                  }
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    setActiveIndex((prev) => Math.max(prev - 1, 0));
                  }
                  if (event.key === 'Enter' && visibleRows[activeIndex]) {
                    event.preventDefault();
                    openResult(visibleRows[activeIndex]);
                  }
                }}
              />
            </SearchWrap>
          </Head>

          <Body>
            {showHistory ? (
              <Section>
                <SectionHead>
                  <Title>Recent searches</Title>
                  {historyItems.length > 0 ? (
                    <ClearBtn type="button" onClick={clearHistory}>
                      <Trash2 size={14} />
                      Clear
                    </ClearBtn>
                  ) : null}
                </SectionHead>
                <SearchState>Start typing to search users, emails, clients, businesses, and ticket numbers.</SearchState>
                {historyItems.length > 0 ? (
                  <ResultList>
                    {historyItems.map((entry, index) => (
                      <ResultRow
                        key={`${entry.ref}-${entry.searchedAt}`}
                        type="button"
                        $active={index === activeIndex}
                        onClick={() => openResult(entry)}
                      >
                        <PortalAvatar
                          uid={entry.uid}
                          email={entry.email}
                          firstName={entry.firstName}
                          lastName={entry.lastName}
                          name={entry.name}
                          photoURL={entry.photoURL}
                          size={40}
                        />
                        <ResultMeta>
                          <ResultName>{entry.name}</ResultName>
                          <ResultSub>
                            <Clock3 size={12} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                            {entry.businessName || entry.email || entry.role || 'User'}
                          </ResultSub>
                        </ResultMeta>
                      </ResultRow>
                    ))}
                  </ResultList>
                ) : null}
              </Section>
            ) : (
              <Section>
                <SectionHead>
                  <Title>Results</Title>
                </SectionHead>
                <SearchState>
                  {loading ? `Searching for "${trimmedQuery}"...` : `Results for "${trimmedQuery}"`}
                </SearchState>
                {loading ? <Empty>Searching...</Empty> : null}
                {!loading && error ? <Empty>{error}</Empty> : null}
                {!loading && !error && visibleRows.length === 0 ? (
                  <Empty>No matches found.</Empty>
                ) : null}
                {!loading && !error && visibleRows.length > 0 ? (
                  <ResultList>
                    {visibleRows.map((result, index) => (
                      <ResultRow
                        key={result.ref}
                        type="button"
                        $active={index === activeIndex}
                        onClick={() => openResult(result)}
                      >
                        {result.kind === 'ticket' ? (
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 10,
                              border: '1px solid rgba(255,255,255,0.14)',
                              display: 'grid',
                              placeItems: 'center',
                              color: '#8DEBFF',
                            }}
                          >
                            <Hash size={16} />
                          </div>
                        ) : (
                          <PortalAvatar
                            uid={result.uid}
                            email={result.email}
                            firstName={result.firstName}
                            lastName={result.lastName}
                            name={result.name}
                            photoURL={result.photoURL}
                            size={40}
                          />
                        )}
                        <ResultMeta>
                          <ResultName>
                            {result.kind === 'ticket'
                              ? `${result.ticketNumber ? `#${result.ticketNumber}` : 'Ticket'} - ${result.title}`
                              : result.name}
                          </ResultName>
                          <ResultSub>
                            {result.kind === 'ticket'
                              ? [result.orgName, result.projectName, result.status]
                                  .filter(Boolean)
                                  .join(' - ') || 'Ticket'
                              : result.businessName || result.email || result.role || (
                                  <>
                                    <UserCircle2 size={12} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                                    User
                                  </>
                                )}
                          </ResultSub>
                        </ResultMeta>
                      </ResultRow>
                    ))}
                  </ResultList>
                ) : null}
              </Section>
            )}
          </Body>
        </Column>
      </Panel>
    </>,
    document.body,
  );
}
